import { NgTemplateOutlet } from '@angular/common';
import { Overlay, type OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { filter } from 'rxjs';

/** Height tiers, low to high. */
export type BottomSheetTier = 'third' | 'half' | 'up' | 'full' | 'screen';
/**
 * Vocabulary `stateChange` reports. Deliberately still the legacy three: 22 call sites hand-type
 * this union in their handler signature, so widening it reddens them all. Callers on the new tiers
 * read the live tier from `state()` / `position()`.
 */
export type BottomSheetState = 'collapsed' | 'half' | 'full';

export const bottomSheetVariants = cva(
  'fixed inset-x-0 bottom-0 z-[100] flex flex-col overflow-hidden rounded-t-[var(--r-xl)] bg-panel shadow-[var(--shadow-4)] ' +
    'transition-[max-height,transform] duration-[var(--t-mid)] ease-[var(--ease-decisive)] ' +
    'will-change-[transform,max-height] motion-reduce:transition-none',
  {
    variants: {
      position: {
        third: 'max-h-[var(--sheet-h-third)]',
        half: 'max-h-[var(--sheet-h-half)]',
        up: 'max-h-[var(--sheet-h-up)]',
        full: 'max-h-[var(--sheet-h-full)]',
        // Full-screen: handle and content start below the status bar.
        screen: 'max-h-dvh pt-[var(--safe-top)]',
      },
      fill: { true: 'h-dvh', false: '' },
    },
    defaultVariants: { position: 'half', fill: false },
  },
);

export type BottomSheetVariants = VariantProps<typeof bottomSheetVariants>;

const TIER_ORDER: readonly BottomSheetTier[] = ['third', 'half', 'up', 'full', 'screen'];
/** Tier heights in dvh. Each mirrors its `--sheet-h-*` token; the spec holds the two in step. */
export const TIER_DVH: Record<BottomSheetTier, number> = { third: 33, half: 42, up: 66, full: 85, screen: 100 };
const DISMISS_DISTANCE = 64;
const FLING_VELOCITY = 0.5;
/** A touch that moves less than this stays a tap — header buttons and tabs under the handle zone keep their click. */
const DRAG_SLOP = 8;
/** Боковой жест объявляет себя раньше вертикального и с запасом: случайный снос пальца при прокрутке — не свайп. */
const SWIPE_AXIS_SLOP = 12;
const SWIPE_AXIS_RATIO = 1.5;
/** Короче — палец только дрогнул; длиннее — это перелистывание. */
const SWIPE_DISTANCE = 48;
/** Marker class on the sheet's own overlay pane — `raise()` finds sibling sheets by it. */
const SHEET_PANE = 'hlm-bottom-sheet-pane';

@Component({
  selector: 'hlm-bottom-sheet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // The sheet itself is fixed (overlay or inline), so the host box is always empty — display:contents keeps it
  // out of the caller's layout; without it the host sizes as a real flex item and eats the caller's remaining space.
  host: { class: 'contents' },
  imports: [NgTemplateOutlet],
  template: `
    @if (inlineHost) {
      <ng-container [ngTemplateOutlet]="sheetTpl" />
    }
    <ng-template #sheetTpl>
      <!-- Dismissal is transform-only, so a closed sheet stays mounted: inert keeps it out of the
           tab order and the a11y tree instead of leaving a focusable dialog off-screen. A sheet mounted open
           is not inert during its entrance: the host dialog's initial focus lands on that first frame. -->
      <div
        role="dialog"
        [attr.inert]="isOpen() || entering ? null : ''"
        [class]="sheetClass()"
        [style.transform]="sheetTransform()"
        [style.transition]="dragging() ? 'none' : ''"
        [style.max-height.px]="dragHeight()"
      >
        <!-- Handle zone = pill + whatever the consumer marks hlmSheetHeader (tabs row, title). -->
        <div
          class="shrink-0"
          (touchstart)="onTouchStart($event)"
          (touchmove)="onTouchMove($event)"
          (touchend)="onTouchEnd()"
          (touchcancel)="onTouchEnd()"
        >
          <div class="flex cursor-grab touch-none justify-center pt-3 pb-2">
            <div class="h-1.25 w-11 rounded-full bg-line3"></div>
          </div>
          <ng-content select="[hlmSheetHeader]" />
        </div>
        <div
          class="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain px-4 pb-[var(--safe-bottom-md)]
            [-webkit-overflow-scrolling:touch]"
          (touchstart)="onTouchStart($event)"
          (touchmove)="onTouchMove($event, false)"
          (touchend)="onTouchEnd()"
          (touchcancel)="onTouchEnd()"
        >
          <ng-content />
        </div>
      </div>
      @if (backdrop() && isOpen()) {
        <div data-scrim class="fixed inset-0 z-[99] bg-[var(--scrim-modal)]" (click)="collapse()"></div>
      }
    </ng-template>
  `,
})
export class HlmBottomSheet implements OnInit, AfterViewInit, OnDestroy {
  readonly initialState = input<'collapsed' | BottomSheetTier>('half');
  /** Tiers the handle drag walks through, low to high; below the lowest the sheet closes. */
  readonly tiers = input<BottomSheetTier[]>(['half']);
  readonly backdrop = input(true);
  /** While false, the drag-down and the backdrop tap spring the sheet back instead of dismissing it. */
  readonly dismissible = input(true);
  /** Panel height = its tier (`h-dvh` under the tier's max-height), whatever the content's own height. */
  readonly fill = input(false, { transform: booleanAttribute });
  readonly translateY = signal(0);
  readonly stateChange = output<BottomSheetState>();
  /** Боковой свайп по листу: `next` — влево (следующая вкладка), `prev` — вправо. Куда переключать, решает хозяин. */
  readonly swipe = output<'next' | 'prev'>();

  /** Height tier while visible; dismissal is transform-only so the sheet never shrinks to a stub. */
  readonly position = signal<BottomSheetTier>('half');
  readonly isOpen = signal(false);
  readonly dragging = signal(false);
  /** Live height while the finger pulls up; null = the tier's own max-height. */
  readonly dragHeight = signal<number | null>(null);
  readonly state = computed<'collapsed' | BottomSheetTier>(() => (this.isOpen() ? this.position() : 'collapsed'));
  private readonly ladder = computed(() => [...this.tiers()].sort((a, b) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b)));
  /** Лестница, оставленная по умолчанию (`['half']`): у четырёх прежних вызовов «выше» значит `full`.
   *  Кто объявил свою одиночную ступень, выше неё не растёт — он назвал ровно ту высоту, что ему нужна. */
  private readonly defaultLadder = computed(() => {
    const ladder = this.ladder();
    return ladder.length < 2 && ladder[0] === 'half';
  });

  protected readonly sheetClass = computed(() => bottomSheetVariants({ position: this.position(), fill: this.fill() }));
  protected readonly sheetTransform = computed(() => (this.isOpen() ? `translateY(${this.translateY()}px)` : 'translateY(105%)'));

  private readonly overlayService = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly sheetTpl = viewChild.required<TemplateRef<unknown>>('sheetTpl');
  private overlayRef: OverlayRef | null = null;
  private readonly mountEl = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  /** Mounted open, entrance still off-screen: kept out of `inert` so the first focus can land inside. */
  protected entering = false;

  /**
   * Inside a BrnDialog the sheet renders inline in the dialog's own overlay pane: a second
   * body-level overlay would make every tap inside the sheet an "outside" pointer event for the
   * dialog (closeOnOutsidePointerEvents) and dismiss it. The dialog pane is already body-level,
   * so the fixed sheet needs no overlay of its own there.
   */
  protected readonly inlineHost = !!inject(BrnDialogRef, { optional: true });

  private startY = 0;
  private startX = 0;
  private axis: 'x' | 'y' | null = null;
  private swipeDelta = 0;
  private startT = 0;
  private dragDelta = 0;
  private lastY = 0;
  private lastT = 0;
  private velocity = 0;

  constructor() {
    // A reused route (RouteReuseStrategy) detaches its page without ngOnDestroy, and the body-level pane would stay
    // over the next page. The pane follows its host: hidden while the host is out of the document, back on reattach.
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    // `events?.`: spec stubs provide Router as a bare `{ navigate }`.
    inject(Router, { optional: true })
      ?.events?.pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.overlayRef?.hostElement.style.setProperty('display', host.isConnected ? '' : 'none'));
  }

  ngOnInit(): void {
    const initial = this.initialState();
    if (initial !== 'collapsed') {
      this.position.set(initial);
      this.entering = true;
    }
  }

  ngAfterViewInit(): void {
    if (!this.inlineHost) {
      // Body-level CDK overlay escapes the caller's stacking context (page content can otherwise paint over the sheet).
      this.overlayRef = this.overlayService.create({
        positionStrategy: this.overlayService.position().global().bottom('0').left('0'),
        scrollStrategy: this.overlayService.scrollStrategies.noop(),
        width: '100%',
        panelClass: SHEET_PANE,
      });
      this.overlayRef.attach(new TemplatePortal(this.sheetTpl(), this.viewContainerRef));
      this.settle();
    }

    if (this.initialState() !== 'collapsed') {
      // Double rAF: first paint lands off-screen, then the slide-up entrance transition plays.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.entering = false;
          this.isOpen.set(true);
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  /** Opens on `tier` when it is in the ladder, else on the lowest tier the caller listed. */
  open(tier?: BottomSheetTier): void {
    const ladder = this.ladder();
    this.setState(tier && ladder.includes(tier) ? tier : (ladder[0] ?? 'half'));
  }

  collapse(): void {
    if (!this.dismissible()) return;
    this.setState('collapsed');
  }

  /** Выше — это верхняя объявленная ступень; лестница по умолчанию по-прежнему растёт до `full`. */
  expand(): void {
    const ladder = this.ladder();
    this.setState(this.defaultLadder() ? 'full' : (ladder[ladder.length - 1] ?? 'full'));
  }

  onTouchStart(e: TouchEvent): void {
    this.startY = this.lastY = e.touches[0].clientY;
    this.startX = e.touches[0].clientX;
    this.startT = this.lastT = performance.now();
    this.velocity = 0;
    this.dragDelta = 0;
    this.swipeDelta = 0;
    this.axis = null;
  }

  /** `tierDrag` = жест начался в зоне ручки, где вертикаль ходит по ступеням. В теле листа листу принадлежит
   *  только горизонталь — вертикаль остаётся прокруткой самого списка. */
  onTouchMove(e: TouchEvent, tierDrag = true): void {
    const y = e.touches[0].clientY;
    const dx = e.touches[0].clientX - this.startX;
    const dy = y - this.startY;
    // Ось решается ОДИН раз за жест: пока она не выбрана, ни лист, ни список палец не забирают.
    if (!this.axis) {
      if (Math.abs(dx) >= SWIPE_AXIS_SLOP && Math.abs(dx) > Math.abs(dy) * SWIPE_AXIS_RATIO) this.axis = 'x';
      else if (Math.abs(dy) >= DRAG_SLOP) this.axis = 'y';
    }

    if (this.axis === 'x') {
      // Свайп вбок принадлежит ящику целиком: ни прокрутки, ни ступени, ни холста под ним.
      this.swipeDelta = dx;
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!tierDrag) return;
    const now = performance.now();
    if (now > this.lastT) this.velocity = (y - this.lastY) / (now - this.lastT);
    this.lastY = y;
    this.lastT = now;
    this.dragDelta = dy;
    // Below the slop the touch is still a tap on a header control; past it the sheet owns the finger.
    if (!this.dragging() && Math.abs(this.dragDelta) < DRAG_SLOP) return;
    this.dragging.set(true);
    if (e.cancelable) e.preventDefault();
    this.translateY.set(Math.max(0, this.dragDelta));
    const multiTier = this.ladder().length > 1;
    this.dragHeight.set(
      multiTier && this.dragDelta < 0 ? Math.min(this.tierPx(this.position()) - this.dragDelta, window.innerHeight) : null,
    );
  }

  onTouchEnd(): void {
    if (this.axis === 'x') {
      const delta = this.swipeDelta;
      this.axis = null;
      this.swipeDelta = 0;
      if (Math.abs(delta) >= SWIPE_DISTANCE) this.swipe.emit(delta < 0 ? 'next' : 'prev');
      return;
    }

    this.axis = null;
    if (!this.dragging()) return;
    this.dragging.set(false);
    this.translateY.set(0);
    this.dragHeight.set(null);
    // A real flick lasts tens of ms; the time gate keeps synthetic same-frame drags distance-only.
    const realGesture = performance.now() - this.startT > 30;
    const flingDown = realGesture && this.velocity > FLING_VELOCITY && this.dragDelta > 12;
    const flingUp = realGesture && this.velocity < -FLING_VELOCITY && this.dragDelta < -12;
    const down = this.dragDelta > DISMISS_DISTANCE || flingDown;
    const up = this.dragDelta < -DISMISS_DISTANCE || flingUp;
    const ladder = this.ladder();
    if (ladder.length < 2) {
      // Single tier keeps the pre-tier behaviour: the default ladder grows to `full`, a named one springs back.
      if (down) {
        const lower = this.neighbourTier(-1);
        if (lower) this.setState(lower);
        else if (this.dismissible()) this.setState('collapsed');
      } else if (up) {
        const higher = this.neighbourTier(1);
        if (higher) this.setState(higher);
      }

      return;
    }

    // Snap to the tier nearest the released height; a decisive pull or a fling moves at least one rung its way.
    const target = this.tierPx(this.position()) - this.dragDelta;
    const cur = ladder.indexOf(this.position());
    let idx = ladder.reduce(
      (best, tier, i) => (Math.abs(this.tierPx(tier) - target) < Math.abs(this.tierPx(ladder[best]) - target) ? i : best),
      0,
    );
    if (up) idx = Math.max(idx, cur + 1);
    if (down) idx = Math.min(idx, cur - 1);
    if (target < this.tierPx(ladder[0]) - DISMISS_DISTANCE) idx = -1;
    if (idx < 0) this.setState(this.dismissible() ? 'collapsed' : ladder[0]);
    else this.setState(ladder[Math.min(idx, ladder.length - 1)]);
  }

  private tierPx(tier: BottomSheetTier): number {
    return (TIER_DVH[tier] * window.innerHeight) / 100;
  }

  /** Next tier up (+1) or down (-1) the caller's ladder; null = spring back, or close when dragging down. */
  private neighbourTier(dir: 1 | -1): BottomSheetTier | null {
    const ladder = this.ladder();
    // The default single-tier ladder keeps the pre-tier behaviour: drag up expands, drag down closes.
    // A caller that named its own single tier springs back — it never grows to a tier it did not declare.
    if (ladder.length < 2) return dir === 1 && this.defaultLadder() ? 'full' : null;
    return ladder[ladder.indexOf(this.position()) + dir] ?? null;
  }

  /**
   * Панели CDK-оверлея стоят на одном z-index — поверх рисуется та, что лежит в контейнере позже.
   * Лист, который открывают, переезжает в конец: иначе лист, смонтированный раньше соседнего
   * (меню ученика в шапке — раньше ящика редактора), открылся бы ПОД ним вместе со своим затемнением.
   * Переезд только за соседние листы: диалоги, меню и прочие оверлеи сохраняют свой порядок.
   */
  private raise(): void {
    const host = this.overlayRef?.hostElement;
    const container = host?.parentElement;
    if (!host || !container) return;
    const sheets = Array.from(container.children).filter(el => el.firstElementChild?.classList.contains(SHEET_PANE));
    const lastSheet = sheets[sheets.length - 1];
    if (!lastSheet || lastSheet === host) return;
    lastSheet.after(host);
    // Переехавший узел теряет вычисленный стиль: чтение раскладки фиксирует стартовое положение за экраном,
    // иначе выезд снизу не играет и лист появляется рывком.
    host.getBoundingClientRect();
  }

  /**
   * A new CDK pane lands last in the container, above dialogs opened before it: a page-mounted sheet that
   * mounts after a modal (phone handoff greeting) would cover it. Page sheets join the sheet layer instead —
   * right after the last sibling sheet, else first. A sheet mounted inside another overlay stays last, above its owner.
   */
  private settle(): void {
    const host = this.overlayRef?.hostElement;
    const container = host?.parentElement;
    if (!host || !container || this.mountEl.closest('.cdk-overlay-container')) return;
    const sheets = Array.from(container.children).filter(
      el => el !== host && el.firstElementChild?.classList.contains(SHEET_PANE),
    );
    const lastSheet = sheets[sheets.length - 1];
    if (lastSheet) lastSheet.after(host);
    else container.prepend(host);
  }

  private setState(newState: 'collapsed' | BottomSheetTier): void {
    if (newState === 'collapsed') {
      this.isOpen.set(false);
    } else {
      // Только на переходе из закрытого: переезд узла посреди перетаскивания оборвал бы его переход.
      if (!this.isOpen()) this.raise();
      this.position.set(newState);
      this.isOpen.set(true);
    }

    // The new tiers stay out of the legacy output vocabulary (see BottomSheetState) — they are read from state().
    if (newState !== 'third' && newState !== 'up' && newState !== 'screen') this.stateChange.emit(newState);
  }
}
